import React, { useState } from 'react';
import {
  Title,
  Text,
  Group,
  Stack,
  Card,
  Button,
  TextInput,
  Select,
  Table,
  Avatar,
  Badge,
  ActionIcon,
  Tooltip,
  Loader,
} from '@mantine/core';
import { PageContainer } from '../../components/ui/PageContainer';
import {
  IconSearch,
  IconEdit,
  IconTrash,
  IconEye,
  IconPlus,
  IconLogin,
  IconUpload,
} from '@tabler/icons-react';
import { ExportButton } from '../../components/ui/ExportButton';
import { ImportModal } from '../../components/ui/ImportModal';
import { UserEditModal } from '../../components/user/UserEditModal';
import { modals } from '@mantine/modals';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, getErrorMessage, apiFormData, ApiError } from '../../utils/api';
import { showToast } from '../../utils/toast';
import { User, Profession } from '../../types';
import { useUserStore } from '../../store/userStore';
import { usePermission } from '../../hooks/usePermission';
import { useTranslation } from 'react-i18next';
import { useDevice } from '../../hooks/useDevice';
import { searchInObject } from '../../utils/search';
import { formatDateShort, formatLastOnline } from '../../utils/date';
import { getActiveStatusColor } from '../../utils/statusUtils';

export const Users: React.FC = () => {
  const { t } = useTranslation();
  const statusLabel = (user: User) =>
    user.active !== false
      ? t('users.active')
      : user.dismissedAt
        ? t('users.archivedSince', { date: formatDateShort(user.dismissedAt) })
        : t('users.inactive');
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  // Уволенные (архив) по умолчанию не мешают: список — работающие люди.
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [professionFilter, setProfessionFilter] = useState<string>('all');
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const deviceType = useDevice();
  const queryClient = useQueryClient();
  const currentUser = useUserStore((s) => s.user);
  // Вход от имени — право company.impersonate (владелец без флага admin тоже).
  const canImpersonate = usePermission('company.impersonate');

  const updateUserMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      return apiFormData(`/users/${editUser?._id}`, {
        method: 'PATCH',
        body: formData,
      });
    },
    onSuccess: () => {
      showToast({
        title: t('users.userUpdated'),
        message: t('users.userUpdatedMsg'),
        color: 'teal',
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error) => {
      const message = getErrorMessage(error, t('users.updateError'));
      showToast({ title: t('common.error'), message, color: 'red' });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return api(`/users/${userId}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      showToast({
        title: t('users.userDeleted'),
        message: t('users.userDeletedMsg'),
        color: 'teal',
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error) => {
      const message = getErrorMessage(error, t('users.deleteError'));
      showToast({ title: t('common.error'), message, color: 'red' });
    },
  });

  const createUserMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      return apiFormData('/users', {
        method: 'POST',
        body: formData,
      });
    },
    onSuccess: () => {
      showToast({
        title: t('users.userCreated'),
        message: t('users.userCreatedMsg'),
        color: 'teal',
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error) => {
      const message = getErrorMessage(error, t('users.createError'));
      showToast({ title: t('common.error'), message, color: 'red' });
    },
  });

  const adminLoginMutation = useMutation({
    mutationFn: async (userId: string) => {
      return api(`/auth/admin-login/${userId}`, {
        method: 'POST',
      });
    },
    onSuccess: (data) => {
      const { token, user } = data;
      useUserStore.getState().impersonateUser(user, token);
      // Кэш собран под правами админа — под другим пользователем он врал бы.
      queryClient.clear();
      showToast({
        title: t('users.adminLoginSuccess'),
        message: t('users.adminLoginSuccessMsg', { name: user.name }),
        color: 'teal',
      });
      // Переходим на главную страницу
      navigate('/');
    },
    onError: (error) => {
      // У цели права шире моих: сервер не пускает смотреть её глазами (вход ⊆ моих прав).
      const isWider =
        error instanceof ApiError &&
        (error.response.data as { code?: string } | undefined)?.code === 'IMPERSONATION_WIDER';
      const message = isWider
        ? t('users.adminLoginWider')
        : getErrorMessage(error, t('users.adminLoginError'));
      showToast({ title: t('common.error'), message, color: 'red' });
    },
  });

  // Получаем список пользователей с API
  const {
    data: users = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      try {
        const response = await api('/users');
        return response;
      } catch (error) {
        showToast({
          title: t('common.error'),
          message: error instanceof Error ? error.message : t('users.errorLoadingUsers'),
          color: 'red',
        });
        throw error;
      }
    },
  });

  const filteredUsers = users.filter((user: User) => {
    const matchesSearch = searchInObject(user, ['name', 'email', 'position'], searchQuery);

    const matchesStatus =
      statusFilter === 'all' ||
      // Старые записи без поля `active` — работающие, как на сервере.
      (statusFilter === 'active' && user.active !== false) ||
      (statusFilter === 'inactive' && user.active === false);

    const matchesProfession =
      professionFilter === 'all' || (user.profession && user.profession._id === professionFilter);

    return matchesSearch && matchesStatus && matchesProfession;
  });

  const handleViewUser = (user: User) => {
    navigate(`/profile/${user._id}`);
  };

  const handleCreateUser = () => {
    setEditUser(null);
    setIsEditMode(false);
    setShowEditModal(true);
  };

  const handleEditUser = (user: User) => {
    setEditUser(user);
    setIsEditMode(true);
    setShowEditModal(true);
  };

  const handleSaveUser = (formData: FormData) =>
    (isEditMode ? updateUserMutation : createUserMutation).mutateAsync(formData);

  const handleDeleteUser = (user: User) => {
    // Проверяем, не пытается ли пользователь удалить самого себя
    if (currentUser && user._id === currentUser._id) {
      showToast({
        title: t('common.error'),
        message: t('users.cannotDeleteSelf'),
        color: 'red',
      });
      return;
    }

    modals.openConfirmModal({
      title: t('users.deleteConfirmTitle', { name: user.name }),
      centered: true,
      children: (
        <Text size="sm">
          <span
            dangerouslySetInnerHTML={{ __html: t('users.deleteConfirmText', { name: user.name }) }}
          />
        </Text>
      ),
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onCancel: () => console.log('Удаление отменено'),
      onConfirm: () => {
        deleteUserMutation.mutate(user._id);
      },
    });
  };

  const handleAdminLogin = (user: User) => {
    if (!canImpersonate) {
      showToast({
        title: t('common.error'),
        message: t('users.adminRequired'),
        color: 'red',
      });
      return;
    }

    // Проверяем, не пытается ли админ авторизоваться от своего имени
    if (currentUser && user._id === currentUser._id) {
      showToast({
        title: t('common.error'),
        message: t('users.cannotImpersonateSelf'),
        color: 'red',
      });
      return;
    }

    modals.openConfirmModal({
      title: t('users.adminLoginConfirmTitle', { name: user.name }),
      centered: true,
      children: <Text size="sm">{t('users.adminLoginConfirmText', { name: user.name })}</Text>,
      labels: { confirm: t('users.adminLogin'), cancel: t('common.cancel') },
      onConfirm: () => {
        adminLoginMutation.mutate(user._id);
      },
    });
  };

  const getStatusColor = getActiveStatusColor;

  return (
    <PageContainer>
      {/* Заголовок */}
      <Group gap="md" align="center" mb={{ base: 'md', sm: 'xl' }}>
        <Title order={2}>{t('users.userList')}</Title>
      </Group>

      <Stack gap="lg">
        {/* Фильтры и поиск */}
        <Card shadow="sm" padding={deviceType === 'mobile' ? 'md' : 'xl'} radius="md" withBorder>
          <Stack gap={deviceType === 'mobile' ? 'md' : 'lg'}>
            <Group
              justify="space-between"
              align="end"
              wrap="wrap"
              gap={deviceType === 'mobile' ? 'sm' : 'md'}
            >
              <Group
                gap={deviceType === 'mobile' ? 'sm' : 'md'}
                style={{ flex: 1, minWidth: 0 }}
                w={deviceType === 'mobile' ? '100%' : 'auto'}
              >
                <TextInput
                  placeholder={t('users.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  leftSection={<IconSearch size={16} />}
                  style={{ flex: 1, minWidth: 0 }}
                  w={deviceType === 'mobile' ? '100%' : 'auto'}
                />
                <Select
                  placeholder={t('users.status')}
                  value={statusFilter}
                  onChange={(value) => setStatusFilter(value || 'all')}
                  data={[
                    { value: 'all', label: t('users.allStatuses') },
                    { value: 'active', label: t('users.active') },
                    { value: 'inactive', label: t('users.inactive') },
                  ]}
                  w={deviceType === 'mobile' ? '100%' : 150}
                />
                <Select
                  placeholder={t('users.selectProfessionFilter')}
                  value={professionFilter}
                  onChange={(value) => setProfessionFilter(value || 'all')}
                  data={[
                    { value: 'all', label: t('users.allProfessions') },
                    ...(users as User[])
                      .map((user) => user.profession)
                      .filter((p: Profession | undefined): p is Profession => !!p)
                      .reduce(
                        (acc: { value: string; label: string }[], profession: Profession) => {
                          if (!acc.find((p) => p.value === profession._id)) {
                            acc.push({ value: profession._id, label: profession.name });
                          }
                          return acc;
                        },
                        [] as { value: string; label: string }[],
                      ),
                  ]}
                  w={deviceType === 'mobile' ? '100%' : 150}
                />
              </Group>
              <Group gap="xs" w={deviceType === 'mobile' ? '100%' : 'auto'}>
                <ExportButton url="/users/export" />
                <Button
                  variant="light"
                  leftSection={<IconUpload size={16} />}
                  onClick={() => setIsImportOpen(true)}
                >
                  {t('importExport.import')}
                </Button>
                <Button
                  leftSection={<IconPlus size={16} />}
                  onClick={handleCreateUser}
                  variant="light"
                  w={deviceType === 'mobile' ? '100%' : 'auto'}
                >
                  {t('users.addUser')}
                </Button>
              </Group>
            </Group>
          </Stack>
        </Card>

        {/* Таблица пользователей */}
        {isLoading ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
          </Group>
        ) : error ? (
          <Card shadow="sm" padding="xl" radius="md" withBorder>
            <Text ta="center" py="xl" c="red">
              {t('users.errorLoadingUsers')}
            </Text>
          </Card>
        ) : filteredUsers.length === 0 ? (
          <Text ta="center" c="dimmed" py="xl">
            {users.length > 0 ? t('common.noSearchResults') : t('users.noUsers')}
          </Text>
        ) : (
          <>
            {/* Десктопная таблица */}
            {deviceType !== 'mobile' && (
              <Card shadow="sm" padding="xl" radius="md" withBorder>
                <div style={{ overflowX: 'auto' }}>
                  <Table miw={900}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>{t('users.user')}</Table.Th>
                        <Table.Th>{t('users.position')}</Table.Th>
                        <Table.Th>{t('users.profession')}</Table.Th>
                        <Table.Th>{t('users.status')}</Table.Th>
                        <Table.Th>{t('users.lastOnline')}</Table.Th>
                        <Table.Th>{t('users.admin')}</Table.Th>
                        <Table.Th>{t('common.actions')}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {filteredUsers.map((user: User) => (
                        <Table.Tr key={user._id}>
                          <Table.Td style={{ minWidth: 220 }}>
                            <Group gap="sm" wrap="nowrap">
                              <Avatar src={user.avatar} radius="xl" style={{ flexShrink: 0 }} />
                              <div style={{ overflow: 'hidden' }}>
                                <Text fw={500} size="sm" truncate>
                                  {user.name}
                                </Text>
                                <Text size="xs" c="dimmed" truncate>
                                  {user.email}
                                </Text>
                              </div>
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{user.position || '—'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{user.profession?.name || '—'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              color={getStatusColor(user.active !== false ? 'active' : 'inactive')}
                              variant="light"
                            >
                              {statusLabel(user)}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">
                              {user.lastOnline
                                ? formatLastOnline(user.lastOnline)
                                : t('users.never')}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge color={user.admin ? 'blue' : 'gray'} variant="light">
                              {user.admin ? t('common.yes') : t('common.no')}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Group gap="xs" wrap="nowrap">
                              <Tooltip label={t('users.view')}>
                                <ActionIcon
                                  variant="light"
                                  onClick={() => handleViewUser(user)}
                                  color="blue"
                                >
                                  <IconEye size={16} />
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip label={t('common.edit')}>
                                <ActionIcon
                                  variant="light"
                                  aria-label={t('common.edit')}
                                  onClick={() => handleEditUser(user)}
                                  color="teal"
                                >
                                  <IconEdit size={16} />
                                </ActionIcon>
                              </Tooltip>

                              {/* Войти как: по праву company.impersonate и только под тем, под кем сервер пустит (canImpersonate) */}
                              {canImpersonate && user.canImpersonate && (
                                <Tooltip label={t('users.adminLogin')}>
                                  <ActionIcon
                                    variant="light"
                                    aria-label={t('users.adminLogin')}
                                    onClick={() => handleAdminLogin(user)}
                                    color="teal"
                                    loading={
                                      adminLoginMutation.isPending &&
                                      adminLoginMutation.variables === user._id
                                    }
                                  >
                                    <IconLogin size={16} />
                                  </ActionIcon>
                                </Tooltip>
                              )}

                              {/* Скрываем кнопку удаления для текущего пользователя */}
                              {(!currentUser || user._id !== currentUser._id) && (
                                <Tooltip label={t('common.delete')}>
                                  <ActionIcon
                                    variant="light"
                                    onClick={() => handleDeleteUser(user)}
                                    color="red"
                                  >
                                    <IconTrash size={16} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </div>
              </Card>
            )}

            {/* Мобильные карточки */}
            {deviceType === 'mobile' && (
              <Stack gap="md">
                {filteredUsers.map((user: User) => (
                  <Card key={user._id} shadow="sm" padding="md" radius="md" withBorder>
                    <Stack gap="sm">
                      {/* Заголовок карточки */}
                      <Group justify="space-between" align="flex-start">
                        <Group gap="sm" wrap="nowrap" style={{ overflow: 'hidden', flex: 1 }}>
                          <Avatar src={user.avatar} radius="xl" style={{ flexShrink: 0 }} />
                          <div style={{ overflow: 'hidden' }}>
                            <Text fw={500} size="sm" truncate>
                              {user.name}
                            </Text>
                            <Text size="xs" c="dimmed" truncate>
                              {user.email}
                            </Text>
                          </div>
                        </Group>
                        <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                          <Tooltip label={t('users.view')}>
                            <ActionIcon
                              variant="light"
                              onClick={() => handleViewUser(user)}
                              color="blue"
                              size="sm"
                            >
                              <IconEye size={16} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label={t('common.edit')}>
                            <ActionIcon
                              variant="light"
                              onClick={() => handleEditUser(user)}
                              color="teal"
                              size="sm"
                            >
                              <IconEdit size={16} />
                            </ActionIcon>
                          </Tooltip>
                          {/* Войти как: по праву company.impersonate и только под тем, под кем сервер пустит (canImpersonate) */}
                          {canImpersonate && user.canImpersonate && (
                            <Tooltip label={t('users.adminLogin')}>
                              <ActionIcon
                                variant="light"
                                aria-label={t('users.adminLogin')}
                                onClick={() => handleAdminLogin(user)}
                                color="purple"
                                size="sm"
                                loading={
                                  adminLoginMutation.isPending &&
                                  adminLoginMutation.variables === user._id
                                }
                              >
                                <IconLogin size={16} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                          {(!currentUser || user._id !== currentUser._id) && (
                            <Tooltip label={t('common.delete')}>
                              <ActionIcon
                                variant="light"
                                onClick={() => handleDeleteUser(user)}
                                color="red"
                                size="sm"
                              >
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </Group>
                      </Group>

                      {/* Детали пользователя */}
                      <Stack gap="xs">
                        <Group gap="xs">
                          <Text size="xs" c="dimmed">
                            {t('users.position')}:
                          </Text>
                          <Text size="xs">{user.position || '—'}</Text>
                        </Group>
                        <Group gap="xs">
                          <Text size="xs" c="dimmed">
                            {t('users.profession')}:
                          </Text>
                          <Text size="xs">{user.profession?.name || '—'}</Text>
                        </Group>
                        <Group gap="xs">
                          <Text size="xs" c="dimmed">
                            {t('users.status')}:
                          </Text>
                          <Badge
                            color={getStatusColor(user.active !== false ? 'active' : 'inactive')}
                            variant="light"
                            size="xs"
                          >
                            {statusLabel(user)}
                          </Badge>
                        </Group>
                        <Group gap="xs">
                          <Text size="xs" c="dimmed">
                            {t('users.lastOnline')}:
                          </Text>
                          <Text size="xs">
                            {user.lastOnline ? formatLastOnline(user.lastOnline) : t('users.never')}
                          </Text>
                        </Group>
                        <Group gap="xs">
                          <Text size="xs" c="dimmed">
                            {t('users.admin')}:
                          </Text>
                          <Badge color={user.admin ? 'blue' : 'gray'} variant="light" size="xs">
                            {user.admin ? t('common.yes') : t('common.no')}
                          </Badge>
                        </Group>
                      </Stack>
                    </Stack>
                  </Card>
                ))}
              </Stack>
            )}
          </>
        )}
      </Stack>

      {/* Модальное окно создания/редактирования пользователя */}
      <UserEditModal
        opened={showEditModal}
        onClose={() => setShowEditModal(false)}
        userData={editUser || undefined}
        onSave={handleSaveUser}
        isEdit={isEditMode}
      />

      <ImportModal
        opened={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        importUrl="/users/import"
        templateUrl="/users/import/template"
        title={t('importExport.import')}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['users'] })}
      />
    </PageContainer>
  );
};
